/**
 * Pickup visuals.
 *
 * Most of the game is walking through a dim room with a torch, so a pickup has
 * exactly one job: be recognised from its outline the instant the beam crosses
 * it. Every item is a distinct silhouette before it is a colour — a carton, a
 * cylinder, a roll, a tent of folded paper — and each sits in a faint pool of
 * light so it registers even at the edge of the cone.
 *
 * Almond water gets the largest pool and the warmest tint on purpose. It is
 * the thing the player is hoping for, and it should look like it.
 *
 * Geometry and materials are built once per kind and reference counted; a
 * pickup's mesh is a `clone()` of that prototype, which shares both.
 */

import * as THREE from 'three';
import { smoothstep, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { ItemKind, ItemSpot } from '../core/types';
import type { ItemView } from './api';

/* ------------------------------------------------------------------ *
 * Shared plumbing
 * ------------------------------------------------------------------ */

interface Disposable {
  dispose(): void;
}

/** Lazily built, reference-counted resources. Freed with the last view holding them. */
class Kit<T extends Disposable> {
  private value: T | null = null;
  private refs = 0;

  constructor(private readonly build: () => T) {}

  acquire(): T {
    if (this.value === null) this.value = this.build();
    this.refs++;
    return this.value;
  }

  release(): void {
    if (this.refs === 0) return;
    if (--this.refs === 0 && this.value !== null) {
      this.value.dispose();
      this.value = null;
    }
  }
}

function freeAll(items: readonly Disposable[]): void {
  for (const item of items) item.dispose();
}

/** Lit normally, plus a whisper of self-illumination so nothing is ever a hole. */
function matte(color: number, emissiveIntensity = 0.09): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, emissive: color, emissiveIntensity });
}

/** Collects everything one pickup's prototype mesh owns, so it can be freed together. */
class BodyParts {
  readonly root = new THREE.Group();
  readonly geometries: THREE.BufferGeometry[] = [];
  readonly materials: THREE.Material[] = [];
  readonly textures: THREE.Texture[] = [];

  part(geo: THREE.BufferGeometry, mat: THREE.Material, parent: THREE.Object3D = this.root): THREE.Mesh {
    if (!this.geometries.includes(geo)) this.geometries.push(geo);
    if (!this.materials.includes(mat)) this.materials.push(mat);
    const mesh = new THREE.Mesh(geo, mat);
    parent.add(mesh);
    return mesh;
  }

  own<T extends THREE.Material>(mat: T): T {
    this.materials.push(mat);
    return mat;
  }

  ownTexture<T extends THREE.Texture>(tex: T): T {
    this.textures.push(tex);
    return tex;
  }

  dispose(): void {
    freeAll(this.geometries);
    freeAll(this.materials);
    freeAll(this.textures);
  }
}

/* ------------------------------------------------------------------ *
 * The pool of light every pickup sits in
 * ------------------------------------------------------------------ */

interface GlowKit extends Disposable {
  plane: THREE.BufferGeometry;
  texture: THREE.Texture;
}

function buildGlowKit(): GlowKit {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.44)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.11)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  const plane = new THREE.PlaneGeometry(1, 1);
  plane.rotateX(-Math.PI / 2);

  return {
    plane,
    texture,
    dispose(): void {
      freeAll([plane, texture]);
    },
  };
}

const glowKit = new Kit(buildGlowKit);

/* ------------------------------------------------------------------ *
 * Per-kind presentation
 * ------------------------------------------------------------------ */

interface ItemStyle {
  /** Tint of the ground pool. */
  glow: number;
  /** Pool diameter in metres. */
  glowSize: number;
  /** Peak pool opacity. */
  glowStrength: number;
  /** Resting height of the body above the floor. */
  lift: number;
  /** Bob amplitude in metres. */
  bob: number;
  /** Yaw drift, radians per second. */
  spin: number;
}

const STYLES: Record<ItemKind, ItemStyle> = {
  'almond-water': { glow: 0xffe6b4, glowSize: 0.78, glowStrength: 0.5, lift: 0.012, bob: 0.013, spin: 0.34 },
  'water-bottle': { glow: 0xbfe8f4, glowSize: 0.56, glowStrength: 0.3, lift: 0.006, bob: 0.007, spin: 0.3 },
  granola: { glow: 0xe0bb7c, glowSize: 0.5, glowStrength: 0.28, lift: 0.005, bob: 0.006, spin: 0.26 },
  'canned-food': { glow: 0xd6cdbe, glowSize: 0.52, glowStrength: 0.28, lift: 0.005, bob: 0.006, spin: 0.28 },
  candy: { glow: 0xffb7c8, glowSize: 0.46, glowStrength: 0.3, lift: 0.005, bob: 0.007, spin: 0.4 },
  battery: { glow: 0xf0d48a, glowSize: 0.44, glowStrength: 0.3, lift: 0.005, bob: 0.006, spin: 0.32 },
  flashlight: { glow: 0xfff0c6, glowSize: 0.62, glowStrength: 0.36, lift: 0.004, bob: 0.005, spin: 0.22 },
  bandage: { glow: 0xf2ece0, glowSize: 0.48, glowStrength: 0.28, lift: 0.004, bob: 0.005, spin: 0.24 },
  pills: { glow: 0xffd0bc, glowSize: 0.48, glowStrength: 0.3, lift: 0.005, bob: 0.006, spin: 0.3 },
  note: { glow: 0xefe6cd, glowSize: 0.52, glowStrength: 0.26, lift: 0.002, bob: 0.004, spin: 0.16 },
  key: { glow: 0xf0d69a, glowSize: 0.46, glowStrength: 0.34, lift: 0.004, bob: 0.006, spin: 0.36 },
};

/* ------------------------------------------------------------------ *
 * Bodies
 * ------------------------------------------------------------------ */

/** Pinch a box's top face flat along Z, turning it into a gable-topped carton. */
function gableTop(geo: THREE.BufferGeometry, ridgeY: number): THREE.BufferGeometry {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    if (pos.getY(i) >= ridgeY - 1e-4) pos.setZ(i, pos.getZ(i) * 0.08);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/** A few lines of ink on a page. Seeded so every note in the game is the same note. */
function makeNoteTexture(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 48;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#e8e1cb';
  ctx.fillRect(0, 0, 64, 48);
  const rng = makeRNG(0x0be7a1);
  ctx.strokeStyle = 'rgba(46,41,33,0.5)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 7; i++) {
    const y = 8 + i * 5;
    ctx.beginPath();
    ctx.moveTo(9 + rng.range(0, 4), y);
    ctx.lineTo(34 + rng.range(0, 21), y);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** A gable-topped carton with a printed band. The only item that is trying to be pretty. */
function buildAlmondWater(p: BodyParts): void {
  const card = p.own(new THREE.MeshLambertMaterial({ color: 0xf4ecdc, emissive: 0xf0e2bc, emissiveIntensity: 0.2 }));
  const print = p.own(matte(0xb98a3c, 0.14));
  const seam = p.own(matte(0xd9d1bf, 0.12));

  const box = new THREE.BoxGeometry(0.076, 0.13, 0.064);
  box.translate(0, 0.065, 0);
  p.part(box, card);

  const gable = gableTop(new THREE.BoxGeometry(0.076, 0.046, 0.064), 0.023);
  gable.translate(0, 0.153, 0);
  p.part(gable, card);

  const fin = new THREE.BoxGeometry(0.072, 0.026, 0.008);
  fin.translate(0, 0.187, 0);
  p.part(fin, seam);

  // Sits proud of the carton rather than coplanar with it, so no z-fighting.
  const band = new THREE.BoxGeometry(0.0778, 0.042, 0.0658);
  band.translate(0, 0.062, 0);
  p.part(band, print);
}

function buildWaterBottle(p: BodyParts): void {
  const plastic = p.own(
    new THREE.MeshLambertMaterial({
      color: 0xcdeaf2,
      emissive: 0x9ecfdd,
      emissiveIntensity: 0.14,
      transparent: true,
      opacity: 0.62,
    })
  );
  const cap = p.own(matte(0x3d78b4, 0.1));

  const profile = [
    new THREE.Vector2(0.0, 0.0),
    new THREE.Vector2(0.03, 0.004),
    new THREE.Vector2(0.034, 0.022),
    new THREE.Vector2(0.031, 0.098),
    new THREE.Vector2(0.033, 0.116),
    new THREE.Vector2(0.028, 0.13),
    new THREE.Vector2(0.015, 0.152),
    new THREE.Vector2(0.0155, 0.168),
  ];
  p.part(new THREE.LatheGeometry(profile, 9), plastic);

  const lid = new THREE.CylinderGeometry(0.018, 0.018, 0.016, 10);
  lid.translate(0, 0.176, 0);
  p.part(lid, cap);
}

function buildGranola(p: BodyParts): void {
  const wrapper = p.own(matte(0xb0803e, 0.1));
  const stripe = p.own(matte(0x6d4a22, 0.08));

  const bar = new THREE.BoxGeometry(0.118, 0.03, 0.05);
  bar.translate(0, 0.015, 0);
  p.part(bar, wrapper);

  for (const side of [-1, 1] as const) {
    const crimp = new THREE.BoxGeometry(0.024, 0.018, 0.052);
    crimp.translate(side * 0.069, 0.015, 0);
    p.part(crimp, wrapper);
  }

  const label = new THREE.BoxGeometry(0.052, 0.0312, 0.0508);
  label.translate(0, 0.015, 0);
  p.part(label, stripe);
}

function buildCannedFood(p: BodyParts): void {
  const steel = p.own(matte(0xa9adb2, 0.1));
  const label = p.own(matte(0xa2432c, 0.1));

  const can = new THREE.CylinderGeometry(0.036, 0.036, 0.086, 12);
  can.translate(0, 0.043, 0);
  p.part(can, steel);

  for (const y of [0.004, 0.082]) {
    const rim = new THREE.CylinderGeometry(0.0385, 0.0385, 0.006, 12);
    rim.translate(0, y, 0);
    p.part(rim, steel);
  }

  const wrap = new THREE.CylinderGeometry(0.0368, 0.0368, 0.056, 12, 1, true);
  wrap.translate(0, 0.043, 0);
  p.part(wrap, label);
}

function buildCandy(p: BodyParts): void {
  const wrapper = p.own(matte(0xd0465f, 0.16));

  const core = new THREE.SphereGeometry(0.019, 8, 6);
  core.scale(1.6, 1.0, 1.0);
  core.translate(0, 0.019, 0);
  p.part(core, wrapper);

  // The twisted ends are what makes it read as a sweet and not a pebble.
  for (const side of [-1, 1] as const) {
    const twist = new THREE.ConeGeometry(0.016, 0.028, 7);
    twist.rotateZ(side * (Math.PI / 2));
    twist.translate(side * 0.044, 0.019, 0);
    p.part(twist, wrapper);
  }
}

function buildBattery(p: BodyParts): void {
  const shell = p.own(matte(0x2a2f38, 0.08));
  const band = p.own(matte(0xc8a02a, 0.16));
  const contact = p.own(matte(0xb9bcc0, 0.12));

  const cell = new THREE.CylinderGeometry(0.0105, 0.0105, 0.049, 10);
  cell.translate(0, 0.0245, 0);
  p.part(cell, shell);

  const stripe = new THREE.CylinderGeometry(0.0109, 0.0109, 0.015, 10);
  stripe.translate(0, 0.036, 0);
  p.part(stripe, band);

  const nub = new THREE.CylinderGeometry(0.0042, 0.0042, 0.004, 8);
  nub.translate(0, 0.051, 0);
  p.part(nub, contact);
}

function buildFlashlight(p: BodyParts): void {
  const shell = p.own(matte(0x30343a, 0.08));
  const bezel = p.own(matte(0x6a6f74, 0.1));
  // Basic, not Lambert: a dead torch on the floor still shows a bright lens,
  // which is the whole reason a player spots one across a room.
  const lens = p.own(new THREE.MeshBasicMaterial({ color: 0xfff1c6 }));

  const barrel = new THREE.CylinderGeometry(0.019, 0.019, 0.105, 10);
  barrel.rotateZ(Math.PI / 2);
  barrel.translate(0.006, 0.019, 0);
  p.part(barrel, shell);

  const grip = new THREE.CylinderGeometry(0.0208, 0.0208, 0.03, 10);
  grip.rotateZ(Math.PI / 2);
  grip.translate(0.028, 0.019, 0);
  p.part(grip, bezel);

  const head = new THREE.CylinderGeometry(0.029, 0.021, 0.04, 10);
  head.rotateZ(Math.PI / 2);
  head.translate(-0.066, 0.019, 0);
  p.part(head, bezel);

  const glass = new THREE.CylinderGeometry(0.026, 0.026, 0.004, 10);
  glass.rotateZ(Math.PI / 2);
  glass.translate(-0.0865, 0.019, 0);
  p.part(glass, lens);
}

function buildBandage(p: BodyParts): void {
  const gauze = p.own(matte(0xece5d6, 0.13));

  // A torus reads as a roll with a hole through it, which nothing else here does.
  const roll = new THREE.TorusGeometry(0.026, 0.019, 5, 12);
  roll.rotateX(Math.PI / 2);
  roll.translate(0, 0.019, 0);
  p.part(roll, gauze);

  const tail = new THREE.BoxGeometry(0.038, 0.0025, 0.028);
  tail.translate(0.055, 0.0025, 0.004);
  p.part(tail, gauze);
}

function buildPills(p: BodyParts): void {
  const bottle = p.own(matte(0xe6e0d2, 0.12));
  const cap = p.own(matte(0xcf5636, 0.12));
  const label = p.own(matte(0xf6f2e8, 0.14));

  const jar = new THREE.CylinderGeometry(0.023, 0.023, 0.062, 10);
  jar.translate(0, 0.031, 0);
  p.part(jar, bottle);

  const wrap = new THREE.CylinderGeometry(0.0236, 0.0236, 0.034, 10, 1, true);
  wrap.translate(0, 0.028, 0);
  p.part(wrap, label);

  const lid = new THREE.CylinderGeometry(0.0258, 0.0258, 0.017, 10);
  lid.translate(0, 0.0705, 0);
  p.part(lid, cap);
}

function buildNote(p: BodyParts): void {
  const texture = p.ownTexture(makeNoteTexture());
  const paper = p.own(
    new THREE.MeshLambertMaterial({
      map: texture,
      color: 0xffffff,
      emissive: 0xd8d0b6,
      emissiveIntensity: 0.16,
      side: THREE.DoubleSide,
    })
  );

  // Tented rather than flat: a sheet lying on carpet has no silhouette at all.
  const tilt = 0.4;
  const half = 0.075;
  const sheet = new THREE.BoxGeometry(0.105, 0.0015, half);
  for (const side of [-1, 1] as const) {
    const leaf = new THREE.Group();
    leaf.position.y = half * Math.sin(tilt);
    leaf.rotation.x = side * tilt;
    const mesh = p.part(sheet, paper, leaf);
    mesh.position.z = side * (half / 2);
    p.root.add(leaf);
  }
}

function buildKey(p: BodyParts): void {
  const brass = p.own(matte(0xb08d4a, 0.2));

  const shaft = new THREE.CylinderGeometry(0.0045, 0.0045, 0.07, 6);
  shaft.rotateZ(Math.PI / 2);
  shaft.translate(0.008, 0.0045, 0);
  p.part(shaft, brass);

  const bow = new THREE.TorusGeometry(0.016, 0.0045, 5, 10);
  bow.rotateX(Math.PI / 2);
  bow.translate(0.058, 0.0045, 0);
  p.part(bow, brass);

  for (const x of [-0.02, -0.032]) {
    const tooth = new THREE.BoxGeometry(0.005, 0.012, 0.005);
    tooth.translate(x, 0.0105, 0);
    p.part(tooth, brass);
  }
}

const BUILDERS: Record<ItemKind, (p: BodyParts) => void> = {
  'almond-water': buildAlmondWater,
  'water-bottle': buildWaterBottle,
  granola: buildGranola,
  'canned-food': buildCannedFood,
  candy: buildCandy,
  battery: buildBattery,
  flashlight: buildFlashlight,
  bandage: buildBandage,
  pills: buildPills,
  note: buildNote,
  key: buildKey,
};

/* ------------------------------------------------------------------ *
 * Kits
 * ------------------------------------------------------------------ */

interface ItemKit extends Disposable {
  style: ItemStyle;
  /** Prototype body; `clone()` shares its geometry and materials. */
  prototype: THREE.Object3D;
  haloGeometry: THREE.BufferGeometry;
  /** Cloned per instance so each pickup fades on its own. */
  haloMaterial: THREE.MeshBasicMaterial;
}

function buildItemKit(kind: ItemKind): ItemKit {
  const style = STYLES[kind];
  const glow = glowKit.acquire();
  const parts = new BodyParts();
  BUILDERS[kind](parts);

  const haloMaterial = new THREE.MeshBasicMaterial({
    map: glow.texture,
    color: style.glow,
    transparent: true,
    opacity: style.glowStrength,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    // Fog would grey the pool out at exactly the range it is meant to work at;
    // the distance falloff below does that job with a curve we control.
    fog: false,
  });

  return {
    style,
    prototype: parts.root,
    haloGeometry: glow.plane,
    haloMaterial,
    dispose(): void {
      parts.dispose();
      haloMaterial.dispose();
      glowKit.release();
    },
  };
}

const kits = new Map<ItemKind, Kit<ItemKit>>();

function kitFor(kind: ItemKind): Kit<ItemKit> {
  let kit = kits.get(kind);
  if (kit === undefined) {
    kit = new Kit(() => buildItemKit(kind));
    kits.set(kind, kit);
  }
  return kit;
}

/* ------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------ */

/**
 * Build the visual for one pickup. The view owns its own placement: it reads
 * `spot.pos` every frame and is authored with the floor at y = 0, so the
 * renderer only has to parent it and call `update`.
 */
export function createItemView(kind: ItemKind): ItemView {
  const cache = kitFor(kind);
  const kit = cache.acquire();
  const style = kit.style;

  const object = new THREE.Group();

  const haloMaterial = kit.haloMaterial.clone();
  const halo = new THREE.Mesh(kit.haloGeometry, haloMaterial);
  halo.scale.set(style.glowSize, 1, style.glowSize);
  halo.position.y = 0.008;
  halo.renderOrder = -1;
  object.add(halo);

  const bobber = new THREE.Group();
  bobber.add(kit.prototype.clone(true));
  object.add(bobber);

  // Per-instance so a shelf of identical cans does not pulse in unison.
  const phase = Math.random() * TAU;

  return {
    object,
    update(spot: ItemSpot, _dt: number, time: number, camera: THREE.Camera) {
      object.position.set(spot.pos.x, 0, spot.pos.z);

      bobber.position.y = style.lift + (0.5 + 0.5 * Math.sin(time * 1.15 + phase)) * style.bob;
      bobber.rotation.y = time * style.spin + phase;

      // The pool has to be invisible from across the level and unmissable from
      // the doorway, so it comes up sharply inside torch range.
      const dist = Math.hypot(camera.position.x - spot.pos.x, camera.position.z - spot.pos.z);
      const near = 1 - smoothstep(9, 24, dist);
      haloMaterial.opacity = style.glowStrength * near * (0.82 + 0.18 * Math.sin(time * 1.5 + phase));
    },
    dispose() {
      object.clear();
      haloMaterial.dispose();
      cache.release();
    },
  };
}
